/**
 * Si el navegador admite lo que Tailwind 4 necesita para pintar la aplicación.
 *
 * 🔴 Esto no es una preferencia de soporte: por debajo del mínimo **se ignora TODO el CSS** y la
 * aplicación se ve sin un solo estilo —texto plano sobre blanco, con los formularios y los
 * botones irreconocibles—. No es «se ve un poco rara»: es ilegible, y en un producto donde la
 * gente gestiona su plan de pensiones eso no se puede dejar pasar sin avisar.
 *
 * Se comprueban CAPACIDADES, no el nombre ni la versión del navegador. La cadena de agente de
 * usuario miente —la falsean extensiones, modos de compatibilidad y navegadores embebidos— y
 * además obliga a mantener una lista que envejece sola. Aquí se pregunta directamente por lo
 * que hace falta.
 *
 * Las tres cosas que Tailwind 4 da por supuestas, y que marcan su mínimo (Safari 16.4, Chrome
 * 111, Firefox 128):
 *
 * - **Capas en cascada** (`@layer`). Es la que rompe del todo: las utilidades viven en
 *   `@layer utilities`, y un navegador que no entiende la regla descarta su contenido entero.
 * - **`color-mix()`**, con el que se calculan las variantes de color.
 * - **`@property`**, del que dependen las animaciones y las transiciones de los componentes.
 */

/** Se exporta para poder probarlo sin un navegador delante. */
export const admiteLoQueTailwindNecesita = (ventana: Window = window): boolean => {
	const css = (ventana as Window & { CSS?: typeof CSS }).CSS;

	// Un navegador tan viejo que ni tiene la API de comprobación tampoco tiene lo demás.
	if (!css || typeof css.supports !== 'function') return false;

	const capas =
		typeof (ventana as unknown as { CSSLayerBlockRule?: unknown }).CSSLayerBlockRule !==
		'undefined';
	const mezclaDeColor = css.supports('color', 'color-mix(in oklab, red, blue)');
	const propiedadesTipadas =
		typeof (css as { registerProperty?: unknown }).registerProperty === 'function';

	return capas && mezclaDeColor && propiedadesTipadas;
};

/**
 * Quien ha decidido seguir de todas formas. Es decisión suya y se respeta, pero dura lo que
 * dure la pestaña: en la siguiente visita vuelve a verlo, porque el problema sigue ahí y el
 * aviso es lo único que lo explica.
 */
const CLAVE_CONTINUAR = 'arca_navegador_avisado';

export const haDecididoSeguir = (): boolean => {
	try {
		return sessionStorage.getItem(CLAVE_CONTINUAR) === 'si';
	} catch {
		// En navegación privada de algunos navegadores, leer `sessionStorage` lanza. Que no se
		// pueda recordar la decisión no es motivo para romper la aplicación: se vuelve a avisar.
		return false;
	}
};

export const recordarQueSigue = (): void => {
	try {
		sessionStorage.setItem(CLAVE_CONTINUAR, 'si');
	} catch {
		// Ídem: si no se puede guardar, el aviso reaparecerá. Molesto, no roto.
	}
};
